import { rowMetrics } from '../GscTools/metrics';
import { escapeRegex } from '../GscTools/regex';

/**
 * The candidate rule, and the arithmetic that applies it to the page rows of one query.
 *
 * Every number here is a local review choice made over rows Search Console already returned.
 * None of them is a Search Analytics filter, so changing one re-reads the evaluations in memory
 * and never costs a request — and none of them is a verdict. A query whose retrieved pages clear
 * the rule is one a person might look at: several of the site's URLs were shown for it. That says
 * nothing about whether they competed, whether either lost anything, or whether consolidating them
 * would help, and the page never suggests a redirect, a canonical or a merge on the strength of it.
 */

/** The choices the rule controls offer, smallest first. Fixed, so there is nothing to mistype. */
export const IMPRESSION_OPTIONS = [200, 500, 1000];
export const SHARE_OPTIONS = [0.15, 0.2, 0.25];
export const URL_OPTIONS = [2, 3];

export const DEFAULT_RULE = { minImpressions: 200, minShare: 0.15, minUrls: 2 };

/**
 * How many queries one evaluation may cover. Each costs one page request, so this is what bounds
 * the report's request budget; it is a product bound, not a tested quota entitlement.
 */
export const MAX_SELECTED = 20;

/** How many page requests are in flight at once while a selection is evaluated. */
export const EVALUATION_CONCURRENCY = 3;

/** What one evaluated query reads as under the rule in force. */
export const STATE = {
    OVERLAP: 'overlap',
    BELOW: 'below',
    NO_IMPRESSIONS: 'no_impressions',
    PENDING: 'pending',
    LOADING: 'loading',
    FAILED: 'failed',
};

/** Block 02's two views: every evaluated query, or only the ones flagged for a navigation check. */
export const REVIEW = { ALL: 'all', NAVIGATION: 'navigation' };

/** The rule an address describes, each value taken only when it is one the controls offer. */
export function ruleFromParams(params) {
    const pick = (value, options, fallback) => {
        const parsed = Number(value);
        return options.includes(parsed) ? parsed : fallback;
    };

    return {
        minImpressions: pick(
            params['min-impressions'],
            IMPRESSION_OPTIONS,
            DEFAULT_RULE.minImpressions,
        ),
        minShare: pick(params['min-share'], SHARE_OPTIONS, DEFAULT_RULE.minShare),
        minUrls: pick(params['min-urls'], URL_OPTIONS, DEFAULT_RULE.minUrls),
    };
}

/**
 * One query's retrieved page rows read under the rule.
 *
 * The share denominator is every page row retrieved for the query — including the URLs that fall
 * below the share threshold — because a share computed after thresholding would inflate the URLs
 * that remain. It is a share of *retrieved page impressions* for this exact query and scope, never
 * of the property's impressions. With no retrieved impressions there is nothing to divide, and the
 * shares are unavailable rather than zero.
 */
export function evaluatePages(rows, rule, hasPosition) {
    const pages = (rows ?? []).map((row) => {
        const key = row?.keys?.[0] ?? '';
        return { id: key, key, ...rowMetrics(row, hasPosition) };
    });

    const total = pages.reduce((sum, page) => sum + page.impressions, 0);

    const withShares = pages
        .map((page) => {
            const share = total > 0 ? page.impressions / total : null;
            return { ...page, share, qualifies: share !== null && share >= rule.minShare };
        })
        .sort((a, b) => b.impressions - a.impressions || (a.key < b.key ? -1 : 1));

    const qualifying = withShares.filter((page) => page.qualifies).length;

    let state = STATE.BELOW;
    if (total === 0) state = STATE.NO_IMPRESSIONS;
    else if (total >= rule.minImpressions && qualifying >= rule.minUrls) state = STATE.OVERLAP;

    return {
        pages: withShares,
        total,
        qualifying,
        largestShare: total > 0 ? withShares[0].share : null,
        state,
    };
}

/** Words that make a query look like a way to reach the site rather than a question about it. */
const NAVIGATION_TERMS = [
    'login',
    'log in',
    'sign in',
    'signin',
    'sign up',
    'signup',
    'my account',
    'account',
    'contact',
];

/**
 * The property's own name, as far as its address says: the label in front of the top-level
 * domain, so `sc-domain:example.com` and `https://www.example.co.uk/` both give "example".
 *
 * A short second-to-last label (co, com, org…) is taken to be part of a two-part suffix and
 * skipped. This is a heuristic for a review aid, not a public-suffix lookup.
 */
export function propertyName(siteUrl) {
    let host = (siteUrl ?? '').replace(/^sc-domain:/, '');
    try {
        if (/^https?:\/\//.test(host)) host = new URL(host).hostname;
    } catch (err) {
        return null;
    }

    const labels = host
        .toLowerCase()
        .replace(/^www\./, '')
        .split('.')
        .filter(Boolean);
    if (labels.length < 2) return labels[0] ?? null;

    labels.pop();
    if (labels.length > 1 && labels[labels.length - 1].length <= 3) labels.pop();
    return labels[labels.length - 1] ?? null;
}

const containsWord = (text, term) =>
    new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegex(term)}($|[^\\p{L}\\p{N}])`, 'iu').test(text);

/**
 * Why one query is flagged for a brand or navigation check, as the word that flagged it, or null.
 *
 * A text heuristic and nothing more, stated as such wherever its result is shown: it looks for the
 * property's own name or a navigation word as a whole word in the query. It is there because a
 * query like "example login" can legitimately send people to more than one of the site's pages,
 * and a reviewer should read those rows differently. It never decides anything — a flagged query
 * keeps its state under the rule, and an unflagged one can still be navigational.
 */
export function navigationReason(query, siteUrl) {
    const name = propertyName(siteUrl);
    if (name && name.length >= 3 && containsWord(query, name)) return name;
    return NAVIGATION_TERMS.find((term) => containsWord(query, term)) ?? null;
}
